import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { FUSEAU_PAR_DEFAUT } from '@taki/data-model';
import { SettingsMenu, SettingsMessage, SettingsRow, SettingsSection, useInstallationPort } from '@taki/ui';
import type { SettingsMenuOption } from '@taki/ui';
import { messageDErreur } from './MembresSettingsPanel.js';

/**
 * PARAMETRES > ENVIRONNEMENT > REGION : le fuseau horaire de l'org.
 *
 * Il decide « aujourd'hui », « hier » et « cette semaine » pour l'app ET pour
 * le serveur (planifications de Flux, notifications, Lia) :
 * `docs/socle/fuseau-org.md`. Il vit dans `settings/environnement`, ecrit par
 * l'owner seul, et passe par le port d'installation que l'hote remplit.
 *
 * Choisir Europe/Luxembourg RETIRE le reglage au lieu de l'ecrire : c'est le
 * defaut, et le figer ne servirait a rien (meme regle que le branding).
 */

/** Le decalage du moment, `UTC+02:00`, pour reconnaitre un fuseau dans la liste. */
function decalage(fuseau: string, maintenant: Date): string {
  const nom = new Intl.DateTimeFormat('en-US', { timeZone: fuseau, timeZoneName: 'longOffset' })
    .formatToParts(maintenant)
    .find((partie) => partie.type === 'timeZoneName')?.value;
  return nom === undefined || nom === 'GMT' ? 'UTC+00:00' : nom.replace('GMT', 'UTC');
}

/** Les fuseaux IANA de ce navigateur, UTC et le fuseau courant compris, tries par nom. */
export function optionsDeFuseaux(maintenant: Date, courant: string): SettingsMenuOption[] {
  const noms = new Set<string>(Intl.supportedValuesOf('timeZone'));
  noms.add('UTC');
  noms.add(FUSEAU_PAR_DEFAUT);
  noms.add(courant);
  return [...noms].sort().map((nom) => ({
    value: nom,
    label: `${nom.replaceAll('_', ' ')} (${decalage(nom, maintenant)})`,
  }));
}

/** Ce qui part dans le document : le defaut s'enregistre a `null`. */
export function fuseauAEnregistrer(choisi: string): string | null {
  return choisi === FUSEAU_PAR_DEFAUT ? null : choisi;
}

export function FuseauSettingsSection(): JSX.Element | null {
  const port = useInstallationPort();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const courant = port?.fuseau ?? FUSEAU_PAR_DEFAUT;
  const options = useMemo(() => optionsDeFuseaux(new Date(), courant), [courant]);
  if (port === null) return null;

  return (
    <SettingsSection title="Région" contextLabel="Organisation">
      <SettingsRow label="Fuseau horaire" lock={port.peutChangerLeFuseau ? undefined : 'Réservé au propriétaire.'}>
        <SettingsMenu
          label="Fuseau horaire"
          value={courant}
          options={options}
          disabled={!port.peutChangerLeFuseau || enCours}
          onChange={(valeur) => {
            setErreur(null);
            setEnCours(true);
            port.changerFuseau(fuseauAEnregistrer(valeur)).then(
              () => setEnCours(false),
              (cause: unknown) => {
                setEnCours(false);
                setErreur(messageDErreur(cause));
              },
            );
          }}
        />
      </SettingsRow>
      {erreur != null && <SettingsMessage tone="error" end>{erreur}</SettingsMessage>}
    </SettingsSection>
  );
}
